import axios from 'axios';
import { mapState } from 'vuex';
import PageHeader from '@/components/widgets/PageHeader/PageHeader.vue';
import Loading from '@/components/widgets/Loading/Loading.vue';
import AccountList from '@/components/widgets/AccountList/AccountList.vue';
import { smartFocus } from '@/utils/uiHelper';
import { buildQueryString } from '@/utils/urlHelper';
import { filterAccounts } from '@/utils/appLoginHelper';

// @vue/component
export default {
  name: 'SelectAccount',
  components: {
    PageHeader,
    AccountList,
    Loading,
  },
  data() {
    return {
      accounts: [],
      gad: '',

      isLoading: false,
    };
  },
  created() {
    this.gad = this.$route.query.gad;

    if (!this.gad) {
      this.$store.commit('showFatalError', {
        error: 'Login Failed',
        explanation: 'Invalid parameters, Google token is missing.',
        appError: 'access_denied',
        appErrorExplanation: 'google sso failed, gad token missing',
      });
      return;
    }

    this.getGoogleAccounts();
  },
  computed: {
    ...mapState({
      appInfo: (state) => state.login.appInfo,
      loginType: (state) => state.login.loginType,
      meta: (state) => state.login.meta,
      afterLoginURL: (state) => state.login.afterLoginURL,
      // eslint-disable-next-line
      title: (state) => state.site.siteInfo.displayOptions.showSiteNameOnLoginScreen ? state.site.siteInfo.name : 'Log in',
      inAppLogin: (state) => state.login.loginType === 'app',
    }),
    canSubmit() {
      return this.email.length && this.password.length;
    },
  },
  methods: {
    getGoogleAccounts() {
      this.isLoading = true;

      const data = {
        gad: this.gad,
      };

      // For app login flow, we want to allow apps to hide some accounts,
      // for example if they are already authenticated.
      if (this.inAppLogin && this.meta.ignoreInstallations) {
        data.ignoreInstallations = this.meta.ignoreInstallations;
      }

      if (this.appInfo.redirect_uri) data.redirect_uri = this.appInfo.redirect_uri;
      if (this.appInfo.client_id) data.client_id = this.appInfo.client_id;
      if (this.appInfo.scope) data.scope = this.appInfo.scope;
      if (this.appInfo.install) data.install = this.appInfo.install;
      if (this.appInfo.nonce) data.nonce = this.appInfo.nonce;

      axios.post('/launchpad/v1/googleSSO/accounts.json', data)
      .then((response) => {
        // filter out accounts where provided product is disabled
        // note: we will do this on the backend after global data
        // for all products
        const accs = filterAccounts(response.data.accounts, this.meta.product);

        // If no accounts found, we redirect user to signup,
        // unless they're in app login flow
        if (accs.length === 0) {
          if (this.inAppLogin) {
            this.$store.commit('showFatalError', {
              error: 'Login Failed',
              // eslint-disable-next-line
              explanation: 'We did not find any Teamwork accounts associated with this Google account.',
              appError: 'access_denied',
              appErrorExplanation: 'google sso failed, no teamwork accounts found',
            });
          } else {
            window.location.href = 'https://www.teamwork.com/signup?from=launchpad_google';
          }
          return;
        }

        // If we only have one account, skip selection step
        if (accs.length === 1) {
          return this.onAccountSelected(accs[0]); // eslint-disable-line
        }

        this.accounts = accs;
        this.isLoading = false;
        smartFocus();
      }).catch(() => {
        this.$store.commit('showFatalError', {
          error: 'Login Failed',
          explanation: 'Failed to load your Teamwork accounts, please try again.',
          appError: 'access_denied',
          appErrorExplanation: 'google sso failed, failed to load teamwork accounts',
        });
        this.isLoading = false;
        smartFocus();
      });
    },
    onAccountSelected(account) {
      this.isLoading = true;
      const url = `${account.installation.url}launchpad/tokenauth`;
      const params = {
        code: account.token,
      };

      if (this.afterLoginURL) params.continue = this.afterLoginURL;
      if (this.appInfo.redirect_uri) params.redirect_uri = this.appInfo.redirect_uri;
      if (this.appInfo.state) params.state = this.appInfo.state;
      if (this.appInfo.client_id) params.client_id = this.appInfo.client_id;
      if (this.appInfo.scope) params.scope = this.appInfo.scope;
      if (this.appInfo.install) params.install = this.appInfo.install;
      if (this.appInfo.nonce) params.nonce = this.appInfo.nonce;

      if (this.meta.source) params.source = this.meta.source;
      if (this.meta.product) params.product = this.meta.product;
      if (this.meta.extra) params.extra = this.meta.extra;

      window.location.href = `${url}?${buildQueryString(params)}`;
    },
    reload() {
      window.location.reload();
    },
    filterAccounts(accounts) {
      let key = '';
      switch (this.meta.product) {
        case 'projects':
          key = 'projectsEnabled';
          break;
        case 'desk':
          key = 'deskEnabled';
          break;
        case 'chat':
          key = 'chatEnabled';
          break;
        default:
          return accounts;
      }
      return accounts.filter((acc) => acc.installation[key]);
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/pages/SelectAccount/SelectAccount.js